import type { ChatCard } from './types'

/**
 * Where clicking a card goes. Ported from iOS `ChatCardRoute`; pay-ups have no page of
 * their own, so their card opens the Expenses page on the pay-up's details.
 */
export type ChatCardRoute =
	| { type: 'event'; eventId: number; dayIndex: number | null }
	| { type: 'idea'; ideaId: number }
	| { type: 'expense'; expenseId: number }
	| { type: 'payUp'; payUpId: number }
	| { type: 'flight'; flightGroupId: number }
	| { type: 'accommodation'; accommodationId: number }

/** Null when the card opens nothing: a removed resource, a member, or a kind this build doesn't know. */
export const cardRoute = (card: ChatCard): ChatCardRoute | null => {
	if (card.removed) return null
	switch (card.kind) {
		case 'TripEvent':
			return { type: 'event', eventId: card.id, dayIndex: card.tripDayIndex }
		case 'Idea':
			return { type: 'idea', ideaId: card.id }
		case 'Expense':
			return { type: 'expense', expenseId: card.id }
		case 'PayUp':
			return { type: 'payUp', payUpId: card.id }
		case 'FlightGroup':
			return { type: 'flight', flightGroupId: card.id }
		case 'Accommodation':
			return { type: 'accommodation', accommodationId: card.id }
		case 'Member':
		case 'Other':
			return null
	}
}

/** The query parameter a section page opens one resource's details from. */
export const OPEN_PARAM = {
	event: 'event',
	expense: 'expense',
	payUp: 'payUp',
} as const

export type OpenParamKey = keyof typeof OPEN_PARAM

/** The id a section page is asked to open from its query; null when none is, or it isn't an id. */
export const openParamId = (params: URLSearchParams, key: OpenParamKey): number | null => {
	const value = Number(params.get(OPEN_PARAM[key]))
	return Number.isInteger(value) && value > 0 ? value : null
}

/** The URL a route opens, in the trip. */
export const cardRoutePath = (tripId: string, route: ChatCardRoute): string => {
	const trip = `/trips/${tripId}`
	switch (route.type) {
		case 'event': {
			const day = route.dayIndex != null ? `day=${route.dayIndex + 1}&` : ''
			return `${trip}/itinerary?${day}${OPEN_PARAM.event}=${route.eventId}`
		}
		case 'idea':
			return `${trip}/ideas/${route.ideaId}`
		case 'expense':
			return `${trip}/balances?${OPEN_PARAM.expense}=${route.expenseId}`
		case 'payUp':
			return `${trip}/balances?${OPEN_PARAM.payUp}=${route.payUpId}`
		case 'flight':
			return `${trip}/flights/${route.flightGroupId}`
		case 'accommodation':
			return `${trip}/accommodations/${route.accommodationId}`
	}
}

/** The Chat page, opened on a Message when one is named. */
export const chatPath = (tripId: string | number, messageId?: number): string =>
	`/trips/${tripId}/chat${messageId != null ? `?message=${messageId}` : ''}`
